import { Box, Button, Stack, TextField, CircularProgress } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import Widget from '@/components/common/widget.tsx';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { UpdateFlowDto } from '@/types/dtos/flows/request/update-flow.dto';
import { updateFlowSchemaFactory } from '@/schemas/flows/update-flow';
import { useEventStore } from '@/state/stores/use-event-store';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFlowMode } from '@/components/flows/detail/use-flow-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import type { Mode } from '@/types/entity-mode';
import { Publish } from './actions/publish';
import { useScreenSize } from '@/hooks/use-screen-size';
import { DescriptionFieldWithScrollHint } from '@/components/common/description-field-with-scroll-hint';
import { FieldPublishedBaseline } from '@/components/common/field-published-baseline';
import { changedFieldInputSx } from '@/components/common/changed-field-input.sx';

export type UpdateFlowFormProps = {
  flow: FlowDto;
  updatePending: boolean;
  updateFlow: (flow: UpdateFlowDto) => Promise<void>;
  modeOverride?: Mode;
  /** When set, show plan vs organize comparison inline instead of single-mode view */
  flowPlan?: FlowDto | null;
  flowOrganize?: FlowDto | null;
  /** Show Publish icon in widget header */
  showPublishInHeader?: boolean;
  onShowPublishAlert?: () => void;
};

const UpdateFlowForm = ({
  flow,
  updatePending,
  updateFlow,
  modeOverride,
  flowPlan,
  flowOrganize,
  showPublishInHeader = false,
  onShowPublishAlert,
}: UpdateFlowFormProps) => {
  const t = useTranslations();
  const event = useEventStore((s) => s.event);
  const { isWideScreen } = useScreenSize({ breakpoint: 860 });
  const flowMode = useFlowMode();
  const mode = modeOverride ?? flowMode;
  const updateFlowSchema = updateFlowSchemaFactory(t);
  type FlowFormType = z.infer<typeof updateFlowSchema>;

  const showComparison = !!flowPlan;
  const flowForEdit = showComparison ? flowPlan! : flow;

  const [lastSavedPlan, setLastSavedPlan] = useState<{ title: string; description: string } | null>(null);

  const descriptionRows = isWideScreen ? 8 : 4;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FlowFormType>({
    resolver: zodResolver(updateFlowSchema),
    defaultValues: {
      title: '',
      description: '',
      eventDayId: '',
      eventSlotId: '',
      startTime: undefined,
      endTime: undefined,
    },
  });
  const lastResetKeyRef = useRef<string | null>(null);
  const lastFlowIdRef = useRef<string | null>(null);

  const watchedValues = useWatch({ control });

  const formValues = useMemo(() => {
    if (!flowForEdit) return null;
    return {
      title: flowForEdit.title ?? '',
      description: flowForEdit.description ?? '',
      eventDayId: flowForEdit.slot?.day.id || '',
      eventSlotId: flowForEdit.slot?.id || '',
      startTime:
        flowForEdit.slot && event
          ? minuteOffsetToTime(flowForEdit.slot?.startOffsetMinutes, event.startDate)
          : undefined,
      endTime:
        flowForEdit.slot && flowForEdit.slot?.durationPlannedMinutes && event
          ? minuteOffsetToTime(
              flowForEdit.slot?.startOffsetMinutes + flowForEdit.slot?.durationPlannedMinutes,
              event.startDate,
            )
          : undefined,
    };
  }, [flowForEdit, event]);

  const fieldDiffs = useMemo(() => {
    if (!showComparison || !flowOrganize) {
      return { title: false, description: false };
    }
    const draftTitle = (watchedValues?.title ?? lastSavedPlan?.title ?? flowPlan?.title ?? '').trim();
    const draftDesc = (watchedValues?.description ?? lastSavedPlan?.description ?? flowPlan?.description ?? '').trim();
    const orgTitle = (flowOrganize.title ?? '').trim();
    const orgDesc = (flowOrganize.description ?? '').trim();
    return {
      title: draftTitle !== orgTitle,
      description: draftDesc !== orgDesc,
    };
  }, [showComparison, flowOrganize, flowPlan, lastSavedPlan, watchedValues]);

  const hasPlanOrganizeDiff = useMemo(() => fieldDiffs.title || fieldDiffs.description, [fieldDiffs]);

  useEffect(() => {
    if (!flowPlan) return;
    setLastSavedPlan(null);
  }, [flowPlan?.id]);

  useEffect(() => {
    if (!formValues || !flowForEdit) return;
    const resetKey = JSON.stringify({
      ...formValues,
      flowId: flowForEdit.id,
      flowVersion: flowForEdit.version,
      eventId: event?.id,
      eventStartDate: event?.startDate,
    });
    const flowChanged = lastFlowIdRef.current && lastFlowIdRef.current !== flowForEdit.id;
    if (!flowChanged && (lastResetKeyRef.current === resetKey || isDirty)) return;
    lastResetKeyRef.current = resetKey;
    lastFlowIdRef.current = flowForEdit.id;
    reset(formValues);
  }, [formValues, flowForEdit?.id, flowForEdit?.version, event?.id, event?.startDate, reset, isDirty]);

  const onSubmit = async (data: FlowFormType) => {
    const updateFlowDto: UpdateFlowDto = {
      ...data,
      version: flowForEdit!.version,
      id: flowForEdit!.id,
    };
    try {
      await updateFlow(updateFlowDto);
    } catch {
      return;
    }
    setLastSavedPlan({ title: data.title, description: data.description ?? '' });
    reset(data);
  };

  const handleCancel = useCallback(() => {
    if (!formValues) return;
    reset(formValues);
  }, [formValues, reset]);

  const iconButtons =
    showPublishInHeader && flowPlan ? (
      <Publish
        flow={flow}
        buttonOrIcon="icon"
        context="flowDetailBasics"
        flowPlanOverride={flowPlan}
        onIndicativeClick={onShowPublishAlert}
      />
    ) : undefined;

  const fieldsDisabled = showComparison ? false : isOrganizeMode(mode);
  const showBaselines = showComparison && !!flowOrganize && hasPlanOrganizeDiff;

  const publishedTitle = flowOrganize?.title ?? '';
  const publishedDescription = flowOrganize?.description ?? '';
  // useWatch can briefly return undefined; mirror fieldDiffs fallbacks so baselines
  // don't flash an empty draft while inputs still show plan defaults.
  const draftTitle = (watchedValues?.title ?? lastSavedPlan?.title ?? flowPlan?.title ?? '').trim();
  const draftDescription = (
    watchedValues?.description ??
    lastSavedPlan?.description ??
    flowPlan?.description ??
    ''
  ).trim();

  return (
    <Widget
      title={t('flows.basics')}
      iconButtons={isDirty ? undefined : iconButtons}
      primaryButton={
        isDirty ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={handleCancel}
              data-testid="cancel-updated-flow-button"
            >
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="save-updated-flow-button"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              startIcon={updatePending ? <CircularProgress size={20} /> : undefined}
              disabled={showComparison ? false : isOrganizeMode(mode)}
              size="small"
              color="secondary"
            >
              {t('common.save')}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={1} sx={{ flex: 1 }}>
        <Box>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                multiline
                maxRows={2}
                variant="standard"
                label={t('flows.form.titleInputLabel')}
                placeholder={t('flows.form.titleInputPlaceholder')}
                error={!!errors.title}
                helperText={errors.title?.message}
                disabled={fieldsDisabled}
                sx={showBaselines && fieldDiffs.title ? changedFieldInputSx : undefined}
              />
            )}
          />
          {showBaselines && fieldDiffs.title && (
            <FieldPublishedBaseline publishedValue={publishedTitle} draftValue={draftTitle} />
          )}
        </Box>
        <Box>
          <Controller
            name="description"
            control={control}
            render={({ field }) => {
              const { ref, ...fieldProps } = field;
              return (
                <DescriptionFieldWithScrollHint
                  ref={ref}
                  {...fieldProps}
                  descriptionRows={descriptionRows}
                  disabled={fieldsDisabled}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  label={t('flows.form.descriptionInputLabel')}
                  placeholder={t('flows.form.descriptionInputPlaceholder')}
                  scrollForMoreLabel={t('flows.form.scrollForMore', { defaultValue: 'Scroll for more' })}
                  size="small"
                  sx={showBaselines && fieldDiffs.description ? changedFieldInputSx : undefined}
                />
              );
            }}
          />
          {showBaselines && fieldDiffs.description && (
            <FieldPublishedBaseline publishedValue={publishedDescription} draftValue={draftDescription} />
          )}
        </Box>
      </Stack>
    </Widget>
  );
};

export default UpdateFlowForm;
